import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Chip,
} from '@mui/material';
import { Person, Mail, Visibility, VisibilityOff, Settings } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/profileService';
import { scoutService } from '../../services/scoutService';
import { AthleteUserData } from '../../services/authService';

const AthleteDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [privacyState, setPrivacyState] = useState<string>('private');
  const [scoutCount, setScoutCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const athleteUser = (user ?? {}) as Partial<AthleteUserData>;

  useEffect(() => {
    const load = async () => {
      try {
        // プロフィール状態確認
        try {
          const profileRes = await profileService.getMyProfile();
          setHasProfile(true);
          setPrivacyState(profileRes.data.privacyState || 'private');
        } catch {
          setHasProfile(false);
        }

        // スカウト数
        try {
          const scouts = await scoutService.list('athlete');
          setScoutCount(scouts.length);
          setPendingCount(scouts.filter((s: any) => s.status === 'pending').length);
        } catch {
          // 無視
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                マイページ
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Person sx={{ fontSize: 18 }} />
                <Typography variant="body2">{athleteUser?.name}</Typography>
                {hasProfile && (
                  <Chip
                    icon={privacyState === 'anonymous_public' ? <Visibility /> : <VisibilityOff />}
                    label={privacyState === 'anonymous_public' ? '匿名公開中' : '非公開'}
                    size="small"
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '& .MuiChip-icon': { color: 'white' } }}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            <Button variant="outlined" onClick={logout} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>
              ログアウト
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* スカウト通知 */}
        {pendingCount > 0 && (
          <Card sx={{ mb: 3, border: '2px solid #ff9800', bgcolor: '#fff8e1' }}>
            <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {pendingCount}件の未読スカウトがあります
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  企業からのスカウトメッセージを確認してください
                </Typography>
              </Box>
              <Button variant="contained" onClick={() => navigate('/athlete/scouts')}
                sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}>
                スカウトを確認
              </Button>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* プロフィール */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
              onClick={() => navigate(hasProfile ? '/profile/view' : '/profile/create')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Person sx={{ fontSize: 48, color: '#0066cc', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {hasProfile ? 'プロフィールを見る' : 'プロフィールを作成'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {hasProfile
                    ? 'キャリアプロフィールの確認・公開設定の変更ができます'
                    : '追加の設問に回答して、キャリアプロフィールを生成しましょう'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* スカウト受信箱 */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
              onClick={() => navigate('/athlete/scouts')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Mail sx={{ fontSize: 48, color: '#ff9800', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  スカウト受信箱
                  {scoutCount > 0 && <Chip label={scoutCount} size="small" color="warning" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  企業からのスカウトメッセージの確認・返信ができます
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* プロフィール設定 */}
          {hasProfile && (
            <Grid item xs={12} md={6}>
              <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
                onClick={() => navigate('/profile/settings')}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Settings sx={{ fontSize: 48, color: '#666', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    公開設定
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    プロフィールの公開範囲を変更できます
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 診断を受ける */}
          <Grid item xs={12} md={6}>
            <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
              onClick={() => navigate('/assessment')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  新しい診断を受ける
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  キャリア診断をもう一度受けることができます
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AthleteDashboardPage;
